import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorAlert } from "../components/ErrorAlert";
import { LegalIdentityForm } from "../components/LegalIdentityForm";
import { useTRPC } from "../lib/trpc";

/**
 * The platform default for the legal identity: who every storefront's Terms of Service and
 * Privacy Policy name until its tenant sets its own under Storefront → Legal / Consent. Platform admins
 * only. The platform's own identity until the first save; it cannot be cleared, because a
 * contract with blanks is worse than one naming the platform.
 */
export function PlatformLegalPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.legal.get.queryOptions();
  const query = useQuery(options);
  const save = useMutation(
    trpc.platform.legal.set.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(options.queryKey, saved);
        void queryClient.invalidateQueries({ queryKey: options.queryKey });
        toast.success("Platform legal identity saved.");
      },
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Legal</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The company, contacts and governing law every storefront's Terms of Service and Privacy
          Policy name until its tenant sets its own.
        </p>
      </div>
      {query.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {query.error && <ErrorAlert message={query.error.message} />}
      {query.data && (
        <Card>
          <CardHeader>
            <CardTitle>Platform legal identity</CardTitle>
            <CardDescription>
              Printed into the policy pages of every storefront that has not customized its own. A
              change reaches those pages within a minute.
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-4">
            <LegalIdentityForm
              key={JSON.stringify(query.data.legal)}
              legal={query.data.legal}
              canEdit
              saving={save.isPending}
              onSave={(legal) => save.mutate({ legal })}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
